import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, HStack, Spinner, Text, VStack } from '../../../components/ui';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  DialogActionTrigger, DialogBackdrop, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogPositioner, DialogRoot, DialogTitle
} from '../../../components/ui/dialog';
import { Field } from '../../../components/ui/field';
import { Input } from '../../../components/ui/input';
import { Select } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { FeatureFlag, featureFlagService, UpdateFeatureFlagRequest } from '../../../services/featureFlagService';
import { ServerConfig, serverService } from '../../../services/serverService';
import { ToolConfig, toolService } from '../../../services/toolService';
import { getThemeColor } from '../../../theme/colors';
import { showToast } from '../../../utils/toast';

interface EditFeatureFlagModalProps {
  isOpen: boolean;
  onClose: () => void;
  flag: FeatureFlag;
  onSuccess: () => void;
}

const KEY_REGEX = /^[a-z0-9_]+$/;

const EditFeatureFlagModal: React.FC<EditFeatureFlagModalProps> = ({
  isOpen,
  onClose,
  flag,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<UpdateFeatureFlagRequest>({
    name: flag.name,
    key: flag.key,
    description: flag.description || '',
    category: flag.category || '',
    isActive: flag.isActive
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [servers, setServers] = useState<ServerConfig[]>([]);
  const [selectedServerId, setSelectedServerId] = useState<string>('');
  const [tools, setTools] = useState<ToolConfig[]>([]);
  const [selectedToolIds, setSelectedToolIds] = useState<Set<string>>(new Set());
  const [initialToolIds, setInitialToolIds] = useState<Set<string>>(new Set());
  const [loadingServers, setLoadingServers] = useState(false);
  const [loadingTools, setLoadingTools] = useState(false);
  const [loadingMappings, setLoadingMappings] = useState(false);

  useEffect(() => {
    setFormData({
      name: flag.name,
      key: flag.key,
      description: flag.description || '',
      category: flag.category || '',
      isActive: flag.isActive
    });
  }, [flag]);

  // Load servers on mount
  useEffect(() => {
    const loadServers = async () => {
      try {
        setLoadingServers(true);
        const data = await serverService.getAllServers();
        setServers(data.filter(s => s.isActive));
      } catch (error: any) {
        showToast({
          title: 'Error loading servers',
          description: error.message,
          status: 'error'
        });
      } finally {
        setLoadingServers(false);
      }
    };
    
    if (isOpen) {
      loadServers();
      loadExistingMappings();
    }
  }, [isOpen, flag.key]);

  // Load existing tool mappings
  const loadExistingMappings = async () => {
    try {
      setLoadingMappings(true);
      const mappings = await featureFlagService.getMappingsByFlag(flag.key);
      
      // For simplicity, load the first server's mapping if any exist
      if (mappings.length > 0) {
        const firstMapping = mappings[0];
        setSelectedServerId(firstMapping.serverId);
        const toolIds = new Set(firstMapping.tools.map(t => t.toolId));
        setSelectedToolIds(toolIds);
        setInitialToolIds(new Set(toolIds));
      }
    } catch (error: any) {
      showToast({
        title: 'Error loading tool mappings',
        description: error.message,
        status: 'error'
      });
    } finally {
      setLoadingMappings(false);
    }
  };

  // Load tools when server is selected
  useEffect(() => {
    const loadTools = async () => {
      if (!selectedServerId) {
        setTools([]);
        return;
      }

      try {
        setLoadingTools(true);
        // Clear selected tools when switching servers (except on initial load)
        setSelectedToolIds(new Set());
        const data = await toolService.getServerTools(selectedServerId);
        setTools(data.filter(t => t.isActive));
      } catch (error: any) {
        showToast({
          title: 'Error loading tools',
          description: error.message,
          status: 'error'
        });
      } finally {
        setLoadingTools(false);
      }
    };

    loadTools();
  }, [selectedServerId]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.name && !formData.name.trim()) {
      newErrors.name = t('app.featureFlags.editModal.nameRequired');
    }

    if (formData.key && !formData.key.trim()) {
      newErrors.key = t('app.featureFlags.editModal.keyRequired');
    } else if (formData.key && !KEY_REGEX.test(formData.key)) {
      newErrors.key = t('app.featureFlags.editModal.keyInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      await featureFlagService.updateFlag(flag.id, formData);
      
      // Handle tool mappings if server is selected
      if (selectedServerId) {
        const addedTools = Array.from(selectedToolIds).filter(id => !initialToolIds.has(id));
        const removedTools = Array.from(initialToolIds).filter(id => !selectedToolIds.has(id));
        
        if (addedTools.length > 0) {
          await featureFlagService.assignToolsToFlag(flag.key, selectedServerId, addedTools);
        }
        
        if (removedTools.length > 0) {
          await featureFlagService.removeToolsFromFlag(flag.key, selectedServerId, removedTools);
        }
      }
      
      showToast({
        title: t('app.featureFlags.editModal.updateSuccess'),
        description: `Access policy "${formData.name}" has been updated`,
        status: 'success'
      });
      onSuccess();
      onClose();
    } catch (error: any) {
      showToast({
        title: t('app.featureFlags.editModal.updateError'),
        description: error.response?.data?.message || error.message,
        status: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyChange = (value: string) => {
    // Auto-format: convert to lowercase, replace spaces with underscores
    const formatted = value.toLowerCase().replace(/\s+/g, '_');
    setFormData({ ...formData, key: formatted });
  };

  const handleToolToggle = (toolId: string, checked: boolean) => {
    setSelectedToolIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(toolId);
      } else {
        newSet.delete(toolId);
      }
      return newSet;
    });
  };

  const handleSelectAllTools = () => {
    setSelectedToolIds(new Set(tools.map(t => t.id)));
  };

  const handleDeselectAllTools = () => {
    setSelectedToolIds(new Set());
  };

  return (
    <DialogRoot open={isOpen} onOpenChange={(e: { open: boolean }) => e.open ? undefined : onClose()} size="xl" placement="center">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW="800px" maxH="90vh" display="flex" flexDirection="column">
        <DialogHeader position="sticky" top={0} bg="white" zIndex={1} borderBottomWidth="1px" borderColor="gray.200" _dark={{ bg: "gray.800", borderColor: "gray.700" }}>
          <DialogTitle>{t('app.featureFlags.editModal.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody pb={6} overflowY="auto" flex={1}>
          <VStack gap={4} align="stretch">
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <Field label={t('app.featureFlags.editModal.nameLabel')} invalid={!!errors.name} errorText={errors.name} required>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('app.featureFlags.editModal.namePlaceholder')}
                />
              </Field>

              <Field 
                label={t('app.featureFlags.editModal.keyLabel')} 
                invalid={!!errors.key} 
                errorText={errors.key}
                helperText={t('app.featureFlags.editModal.keyHelper')}
                required
              >
                <Input
                  value={formData.key}
                  onChange={(e) => handleKeyChange(e.target.value)}
                  placeholder={t('app.featureFlags.editModal.keyPlaceholder')}
                  fontFamily="mono"
                />
              </Field>
            </Grid>

            <Field label={t('app.featureFlags.editModal.descriptionLabel')}>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('app.featureFlags.editModal.descriptionPlaceholder')}
              />
            </Field>

            <Field label={t('app.featureFlags.editModal.statusLabel')}>
              <Switch
                checked={formData.isActive}
                onCheckedChange={(e: { checked: boolean }) => setFormData({ ...formData, isActive: e.checked })}
              >
                {formData.isActive ? t('app.featureFlags.editModal.active') : t('app.featureFlags.editModal.inactive')}
              </Switch>
            </Field>

            {/* Server Selection */}
            <Select
              label={t('app.featureFlags.editModal.serverLabel')}
              helperText={t('app.featureFlags.editModal.serverHelper')}
              placeholder={loadingServers || loadingMappings ? t('app.featureFlags.editModal.loadingServers') : t('app.featureFlags.editModal.serverPlaceholder')}
              items={servers.map(s => ({ label: s.name, value: s.id! }))}
              value={selectedServerId ? [selectedServerId] : []}
              onValueChange={(e: any) => {
                const newServerId = e.value[0] || '';
                setSelectedServerId(newServerId);
                // Reset tool selection when changing servers
                if (newServerId !== selectedServerId) {
                  setSelectedToolIds(new Set());
                }
              }}
              disabled={loadingServers || loadingMappings}
            />

            {/* Tool Selection */}
            {selectedServerId && (
              <Field label={t('app.featureFlags.editModal.toolsLabel')} helperText={t('app.featureFlags.editModal.toolsHelper')}>
                {loadingTools ? (
                  <Box p={4} textAlign="center">
                    <Spinner size="sm" mr={2} />
                    <Text display="inline" fontSize="sm" color="gray.600">{t('app.featureFlags.editModal.loadingTools')}</Text>
                  </Box>
                ) : tools.length === 0 ? (
                  <Box p={4} bg="gray.50" borderRadius="md">
                    <Text fontSize="sm" color="gray.600">{t('app.featureFlags.editModal.noTools')}</Text>
                  </Box>
                ) : (
                  <Box>
                    <HStack justify="space-between" mb={2}>
                      <Text fontSize="sm" color="gray.600">
                        {t('app.featureFlags.editModal.toolsSelected', { selected: selectedToolIds.size, total: tools.length })}
                      </Text>
                      <HStack gap={2}>
                        <Button size="xs" variant="ghost" onClick={handleSelectAllTools}>
                          {t('app.featureFlags.editModal.selectAll')}
                        </Button>
                        <Button size="xs" variant="ghost" onClick={handleDeselectAllTools}>
                          {t('app.featureFlags.editModal.deselectAll')}
                        </Button>
                      </HStack>
                    </HStack>
                    <Box 
                      maxH="300px" 
                      overflowY="auto"
                      borderRadius="md" 
                      p={3}
                      bg="gray.50"
                      _dark={{ bg: "gray.900", borderColor: "gray.700" }}
                    >
                      <VStack align="stretch" gap={2}>
                        {tools.map((tool) => (
                          <Box
                            key={tool.id}
                            p={2}
                            bg="white"
                            borderRadius="md"
                            borderColor={selectedToolIds.has(tool.id) ? 'blue.200' : 'gray.200'}
                            _dark={{ bg: "gray.800", borderColor: selectedToolIds.has(tool.id) ? 'blue.500' : 'gray.700' }}
                            cursor="pointer"
                            onClick={() => handleToolToggle(tool.id, !selectedToolIds.has(tool.id))}
                            _hover={{ borderColor: selectedToolIds.has(tool.id) ? 'blue.300' : 'gray.300' }}
                          >
                            <Checkbox
                              checked={selectedToolIds.has(tool.id)}
                              onCheckedChange={(e: any) => {
                                e.stopPropagation?.();
                                handleToolToggle(tool.id, !!e.checked);
                              }}
                              pointerEvents="none"
                            >
                              <VStack align="start" gap={0}>
                                <Text fontWeight="medium" fontSize="sm">{tool.name}</Text>
                                {tool.description && (
                                  <Text fontSize="xs" color="gray.600" _dark={{ color: "gray.400" }}>{tool.description}</Text>
                                )}
                              </VStack>
                            </Checkbox>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                    {errors.tools && selectedToolIds.size === 0 && (
                      <Text color="red.500" fontSize="sm" mt={2}>
                        {errors.tools}
                      </Text>
                    )}
                  </Box>
                )}
              </Field>
            )}

            <Box p={3} bg={getThemeColor('alert.warning.bg')} borderRadius="md" borderColor={getThemeColor('alert.warning.border')} borderWidth="1px">
              <Text fontSize="sm" color={getThemeColor('alert.warning.text')}>
                <strong>{t('app.featureFlags.editModal.warningTitle')}</strong> {t('app.featureFlags.editModal.warningMessage')}
              </Text>
            </Box>
          </VStack>
        </DialogBody>
        <DialogFooter position="sticky" bottom={0} bg="white" zIndex={1} borderTopWidth="1px" borderColor="gray.200" _dark={{ bg: "gray.800", borderColor: "gray.700" }}>
          <DialogActionTrigger asChild>
            <Button variant="outline" onClick={onClose}>
              {t('app.common.cancel')}
            </Button>
          </DialogActionTrigger>
          <Button
            colorPalette="blue"
            onClick={handleSubmit}
            loading={loading}
          >
            {t('app.featureFlags.editModal.saveButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  );
};

export default EditFeatureFlagModal;
